<template>
  <div>
    <b-tabs content-class="border-right border-left border-bottom rounded-bottom">
      <b-tab
        v-for="(editable_data, editable_index) in editableDatasets"
        :key="`edit-chart-data-${editable_index}`"
        :title="editable_data.label"
      >
        <div class="p-3">
          <b-row>
            <b-col cols="12" md="2">
              <b-form-group
                :id="`fieldset-input-${editable_index}-config-type`"
                label="차트 타입"
                :label-for="`input-${editable_index}-config-type`"
                aria-describedby="input-feedback-type"
                :state="validation.type.state"
              >
                <b-form-select
                  :id="`input-${editable_index}-config-type`"
                  v-model="editable_data.type"
                  :options="chart_type_options"
                  @change="updateChart_type($event, editable_index)"
                />
              </b-form-group>

              <b-form-invalid-feedback id="input-feedback-type">
                {{ validation.type.feedback }}
              </b-form-invalid-feedback>
            </b-col>

            <template v-if="editableOptionProps.yAxisID.includes(editable_data.type)">
              <b-col cols="12" md="2">
                <b-form-group
                  :id="`fieldset-input-${editable_index}-config-y-axis`"
                  label="Y축 선택"
                  :label-for="`input-${editable_index}-config-y-axis`"
                  aria-describedby="input-feedback-y-axis"
                  :state="validation.yAxisID.state"
                  :disabled="single_chart.includes(editable_data.type)"
                >
                  <b-form-select
                    :id="`input-${editable_index}-config-y-axis`"
                    v-model="editable_data.yAxisID"
                    :options="y_options"
                    :disabled="single_chart.includes(editable_data.type)"
                    @change="updateChartDataset_YAxis(editable_index, $event)"
                  />
                </b-form-group>

                <b-form-invalid-feedback id="input-feedback-y-axis">
                  {{ validation.yAxisID.feedback }}
                </b-form-invalid-feedback>
              </b-col>
            </template>

            <template v-if="editableOptionProps.barPercentage.includes(editable_data.type)">
              <b-col cols="12" md="2">
                <b-form-group
                  :id="`fieldset-input-${editable_index}-config-barPercentage`"
                  label="막대 너비"
                  :label-for="`input-${editable_index}-config-barPercentage`"
                  aria-describedby="input-feedback-barPercentage"
                  :state="validation.barPercentage.state"
                >
                  <b-form-spinbutton
                    :id="`input-${editable_index}-config-barPercentage`"
                    v-model="editable_data['barPercentage']"
                    @input="updateChart_default(editable_index, 'barPercentage')"
                    :min="0.1"
                    :max="1"
                    :step="0.1"
                  />
                </b-form-group>

                <b-form-invalid-feedback id="input-feedback-barPercentage">
                  {{ validation.type.feedback }}
                </b-form-invalid-feedback>
              </b-col>
            </template>

            <template>
              <b-col cols="12" md="4">
                <b-form-group
                  :id="`fieldset-input-${editable_index}-config-y-axis-unit`"
                  label="Y축 단위"
                  :label-for="`input-${editable_index}-config-y-axis-unit`"
                >
                  <b-form-input
                    :id="`input-${editable_index}-config-y-axis-unit`"
                    v-model="editable_data.unit"
                    @input="updateChart_default(editable_index, 'unit')"
                  />
                </b-form-group>
              </b-col>
            </template>

            <template v-if="editableOptionProps.borderWidth.includes(editable_data.type)">
              <b-col cols="12" md="2">
                <b-form-group
                  :id="`fieldset-input-${editable_index}-config-border-width`"
                  label="선 굵기"
                  :label-for="`input-${editable_index}-config-border-width`"
                  aria-describedby="input-feedback-border-width"
                  :state="validation.borderWidth.state"
                >
                  <b-form-input
                    type="number"
                    :id="`input-${editable_index}-config-border-width`"
                    v-model="editable_data['borderWidth']"
                    @input="updateChart_default(editable_index, 'borderWidth')"
                  />
                </b-form-group>

                <b-form-invalid-feedback id="input-feedback-border-width">
                  {{ validation.type.feedback }}
                </b-form-invalid-feedback>
              </b-col>
            </template>

            <b-col cols="12">
              <hr />
            </b-col>

            <b-col cols="12">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h5>색 설정</h5>

                <div
                  v-b-tooltip.hover.bottom
                  title="기본설정에서 설정한 보고서의 메인/서브 컬러에 따라서 조정됩니다."
                >
                  <b-btn
                    @click="getAutomaticColorScheme(editable_data, false)"
                    size="sm"
                    class="mr-2"
                    >{{ editable_data.label }} 컬러 자동 조정</b-btn
                  >
                  <b-btn
                    @click="getAutomaticColorScheme(editable_data, true)"
                    size="sm"
                    class="mr-2"
                    >개별 컬러 자동 조정</b-btn
                  >
                  <b-btn @click="getAutomaticColorScheme(null, false)" size="sm" class="mr-2"
                    >전체 컬러 자동 조정</b-btn
                  >
                </div>
              </div>

              <b-row>
                <b-col
                  v-for="_b_dx in editable_data.data.length"
                  :key="`dataset-${editable_index}-colorSet-${_b_dx - 1}`"
                  class="border border-light rounded bg-light color-setting-card m-3"
                >
                  <div class="border-bottom border-light text-center p-2">
                    <h6 class="mb-0">{{ labels[_b_dx - 1] }}</h6>
                  </div>
                  <div class="p-2">
                    <div>
                      <template
                        v-if="!!editable_data.borderColor && !!editable_data.borderColor[_b_dx - 1]"
                      >
                        <b-dropdown
                          size="lg"
                          variant="link"
                          toggle-class="text-decoration-none p-0"
                          menu-class="p-0"
                          no-caret
                          block
                          @hidden="updateChartData_color(editable_index, _b_dx - 1, 'borderColor')"
                        >
                          <template #button-content>
                            <div class="border border-light rounded">
                              <div
                                class="h6 rounded text-white px-3 py-2 mb-0"
                                :style="{
                                  backgroundColor: convertingColor(
                                    editable_data.borderColor[_b_dx - 1]
                                  ),
                                }"
                              >
                                선색
                              </div>
                            </div>

                          </template>

                          <b-dropdown-form class="p-0" form-class="p-0">
                            <b-form-group
                              :id="`fieldset-input-${editable_index}-borderColor`"
                              :label-for="`input-${editable_index}-borderColor`"
                              class="mb-0"
                            >
                              <chrome-picker
                                :id="`input-${editable_index}-borderColor`"
                                v-model="editable_data.borderColor[_b_dx - 1]"
                              />
                            </b-form-group>
                          </b-dropdown-form>
                        </b-dropdown>
                      </template>
                      <template v-else>
                        <b-btn
                          size="sm"
                          variant="light"
                          class="mb-2"
                          @click="createDataColor(editable_index, _b_dx - 1, 'borderColor')"
                          block
                        >
                          선색 생성
                        </b-btn>
                      </template>
                    </div>

                    <div>
                      <template
                        v-if="
                          editable_data.backgroundColor && editable_data.backgroundColor[_b_dx - 1]
                        "
                      >
                        <b-dropdown
                          size="lg"
                          variant="link"
                          toggle-class="text-decoration-none p-0"
                          menu-class="p-0"
                          no-caret
                          block
                          @hidden="
                            updateChartData_color(editable_index, _b_dx - 1, 'backgroundColor')
                          "
                        >
                          <template #button-content>
                            <div class="border border-light rounded">
                              <div
                                class="h6 rounded text-white px-3 py-2 mb-0"
                                :style="{
                                  backgroundColor: convertingColor(
                                    editable_data.backgroundColor[_b_dx - 1]
                                  ),
                                }"
                              >
                                배경색
                              </div>
                            </div>
                          </template>

                          <b-dropdown-form class="p-0" form-class="p-0">
                            <b-form-group
                              :id="`fieldset-input-${editable_index}-backgroundColor`"
                              :label-for="`input-${editable_index}-backgroundColor`"
                              class="mb-0"
                            >
                              <chrome-picker
                                :id="`input-${editable_index}-backgroundColor`"
                                v-model="editable_data.backgroundColor[_b_dx - 1]"
                              />
                            </b-form-group>
                          </b-dropdown-form>
                        </b-dropdown>
                      </template>
                      <template v-else>
                        <b-btn
                          size="sm"
                          variant="light"
                          class="mb-2"
                          @click="createDataColor(editable_index, _b_dx - 1, 'backgroundColor')"
                          block
                        >
                          배경색 생성
                        </b-btn>
                      </template>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { Chrome } from 'vue-color';
  import debounce from 'lodash.debounce';
  import tinycolor from 'tinycolor2';
  import { getColorScheme } from './chart';
  import { chart_type_options } from './chart';
  import { y_options, option_scales_common } from '@/util/chart/chart.utils.js';

  export default {
    name: 'EditChartLocalConfig',
    components: {
      'chrome-picker': Chrome,
    },
    props: {
      datasets: {
        type: Array,
        required: true,
        default: () => [],
      },
      labels: {
        type: [Array, Object],
        required: true,
        default: () => [],
      },
      options: {
        type: Object,
        required: true,
        default: () => ({}),
      },
    },
    data() {
      return {
        chart_type_options,
        y_options,
        option_scales_common,
        editableDatasets: [],
        editableOptions: {},
        single_chart: ['pie', 'doughnut', 'polarArea', 'radar'],
        multi_chart: ['line', 'bar', 'mixed'],
        
        // Form helper Data
        validation: {
          type: {
            state: null,
            feedback: null,
          },
          yAxisID: {
            state: null,
            feedback: null,
          },
          borderWidth: {
            state: null,
            feedback: null,
          },
          barPercentage: {
            state: null,
            feedback: null,
          },
        },
      };
    },
    computed: {
      ...mapGetters('report_list', ['selected_report']),
      editableOptionProps() {
        return {

          // Scales Option
          yAxisID: ['bar', 'line', 'mixed'],
          beginAtZero: ['bar', 'line', 'polarArea', 'radar', 'mixed'],
          min: ['bar', 'line', 'polarArea', 'radar', 'mixed'],
          max: ['bar', 'line', 'polarArea', 'radar', 'mixed'],

          // Dataset Option
          barPercentage: ['bar'],
          stack: ['bar', 'line', 'mixed'],
          borderRadius: ['bar', 'doughnut', 'pie', 'polarArea', 'radar', 'mixed'],
          borderWidth: ['bar', 'line', 'doughnut', 'pie', 'polarArea', 'radar', 'mixed'],
          backgroundColor: ['bar', 'line', 'doughnut', 'pie', 'polarArea', 'radar', 'mixed'],
          borderColor: ['bar', 'line', 'doughnut', 'pie', 'polarArea', 'radar', 'mixed'],
          unit: ['bar', 'line', 'doughnut', 'pie', 'polarArea', 'radar', 'mixed'],
        };
      },
    },
    watch: {
      datasets: {
        deep: true,
        handler(_updated_datasets) {
          this.editableDatasets = this.preprocessingDatasets(_updated_datasets);
        },
      },
      options: {
        deep: true,
        handler(_updated_options) {
          this.editableOptions = JSON.parse(JSON.stringify(_updated_options));
        },
      },
    },
    created() {
      this.editableDatasets = this.preprocessingDatasets(this.datasets);
      this.editableOptions = JSON.parse(JSON.stringify(this.options));
    },
    methods: {
      updateChart_default: debounce(function (_edit_data_idx, _prop) {
        let _editableDatasets = JSON.parse(JSON.stringify([...this.editableDatasets]));

        if (['backgroundColor', 'borderColor'].includes(_prop)) {
          _editableDatasets[_edit_data_idx][_prop] = this.cleansingColor(_edit_data_idx, _prop);
        }

        this.$emit('updateChartDataSets', _editableDatasets);
      }, 450),
      preprocessingDatasets(_datasets) {
        let _editableDatasets = JSON.parse(JSON.stringify(_datasets));
        if (_editableDatasets.length) {
          _editableDatasets = _editableDatasets.reduce((_datasets, _data) => {
            if (this.editableOptionProps.yAxisID.includes(_data.type)) {
              _data.yAxisID = !_data.yAxisID ? 'y' : _data.yAxisID;
            }

            if (!_data.borderColor) {
              _data.borderColor = [...Array(_data.data.length).keys()];
              _data.borderColor.fill('#dedede');
            }

            if (!_data.backgroundColor) {
              _data.backgroundColor = [...Array(_data.data.length).keys()];
              _data.backgroundColor.fill('#dedede');
            }

            _datasets.push(_data);
            return _datasets;
          }, []);
        }

        return _editableDatasets;
      },
      updateChart_type(changed_type, changed_data_index) {
        let _chart_type = null;
        let _editableOptions = JSON.parse(JSON.stringify(this.options));
        let _editableDatasets = JSON.parse(JSON.stringify([...this.editableDatasets]));

        if (['line', 'bar'].includes(changed_type)) {
          _chart_type = changed_type;
          _editableDatasets = _editableDatasets.reduce((_dataset, _data) => {
            if (!['line', 'bar'].includes(_data.type)) _data.type = changed_type;
            if (_data.type !== changed_type) _chart_type = 'mixed';
            _dataset.push(_data);
            return _dataset;
          }, []);
        } else {
          _chart_type = changed_type;
          _editableDatasets = _editableDatasets.reduce((_dataset, _data) => {
            _data.type = changed_type;
            _dataset.push(_data);
            return _dataset;
          }, []);
        }

        let _old_type = this.datasets[changed_data_index].type;
        if (_old_type !== changed_type) {
          if (
            this.single_chart.includes(_chart_type) ||
            (this.single_chart.includes(_old_type) && this.multi_chart.includes(_chart_type))
          ) {
            _editableOptions['scales'] = {};
          } else if (
            this.multi_chart.includes(_old_type) &&
            this.single_chart.includes(_chart_type)
          ) {
            if (_editableOptions?.scales.x) delete _editableOptions['scales']['x'];
            if (_editableOptions?.scales.y) delete _editableOptions['scales']['y'];
            if (_editableOptions?.scales['y-right']) delete _editableOptions['scales']['y-right'];
          }
        }

        _editableDatasets = _editableDatasets.reduce((_dataset, _data) => {
          // Scales Option
          if (!this.editableOptionProps.yAxisID.includes(_data.type)) {
            if (_data['yAxisID']) delete _data['yAxisID'];
          }

          if (!this.editableOptionProps.beginAtZero.includes(_data.type)) {
            if (_data['beginAtZero']) delete _data['beginAtZero'];
          }

          if (!this.editableOptionProps.min.includes(_data.type)) {
            if (_data['min']) delete _data['min'];
          }

          if (!this.editableOptionProps.max.includes(_data.type)) {
            if (_data['max']) delete _data['max'];
          }

          // Dataset Option
          if (!this.editableOptionProps.barPercentage.includes(_data.type)) {
            if (_data['barPercentage']) delete _data['barPercentage'];
          }

          if (!this.editableOptionProps.barPercentage.includes(_data.type)) {
            if (_data['stack']) delete _data['stack'];
          }

          if (!this.editableOptionProps.borderRadius.includes(_data.type)) {
            if (_data['borderRadius']) delete _data['borderRadius'];
          }

          _dataset.push(_data);
          return _dataset;
        }, []);

        this.editableDatasets = [..._editableDatasets];
        this.$emit('updateChart', {
          data: { datasets: this.editableDatasets },
          options: _editableOptions,
        });
      },
      updateChartDataset_YAxis() {
        let _editableDatasets = JSON.parse(JSON.stringify([...this.editableDatasets]));
        let _editableOptions = JSON.parse(JSON.stringify({ ...this.editableOptions }));
        const _has_YAxis_right_datasets = _editableDatasets.some(
          ({ yAxisID }) => yAxisID === 'y-right'
        );

        if (!_has_YAxis_right_datasets) {
          _editableDatasets = _editableDatasets.reduce((_datasets, _data) => {
            if (_data.yAxisID) _data.yAxisID = 'y';
            _datasets.push(_data);
            return _datasets;
          }, []);

          delete _editableOptions.scales['y-right'];
        } else {
          if (!_editableOptions.scales['y-right']) {
            _editableOptions.scales['y-right'] = { ...option_scales_common['y-right'] };
          }

          const _is_All_YAxis_right_datasets = _editableDatasets.every(
            ({ yAxisID }) => yAxisID === 'y-right'
          );
          if (_is_All_YAxis_right_datasets || _editableDatasets.length === 1) {
            _editableOptions.scales['y-right'].grid.display = true;
            delete _editableOptions.scales['y'];
          } else {
            _editableOptions.scales['y-right'].grid.display = false;
          }
        }

        this.editableDatasets = _editableDatasets;
        this.editableOptions = _editableOptions;

        this.$emit('updateChart', {
          data: { datasets: _editableDatasets },
          options: _editableOptions,
        });
      },
      updateChartData_color(dataset_idx, data_idx, prop_color) {
        let _updated_color = this.editableDatasets[dataset_idx][prop_color][data_idx];
        _updated_color = this.convertingColor(_updated_color);

        this.editableDatasets[dataset_idx][prop_color][data_idx] = _updated_color;
        this.$emit('updateChartDataSets', [...this.editableDatasets]);
      },
 
      convertingColor(color) {

        if (typeof color === 'string') {
          return color;
        } else if (typeof color === 'object' && 'hex' in color) {
          return color.hex8;
        } else {
          return 'rgba(239, 239, 239, 1)';
        }
      },
      createDataColor(dataset_idx, data_idx, prop_color) {
        const _data_has_no_color =
          !!this.editableDatasets[dataset_idx]['backgroundColor'][data_idx] &&
          !!this.editableDatasets[dataset_idx]['borderColor'][data_idx];
        if (_data_has_no_color) {
          const _new_color = tinycolor.random();
          const [_border_color, _background_color] = [
            _new_color.setAlpha(0.7),
            _new_color.setAlpha(0.5),
          ];

          this.editableDatasets[dataset_idx]['borderColor'][data_idx] = _border_color;
          this.editableDatasets[dataset_idx]['backgroundColor'][data_idx] = _background_color;
        } else {
          let _sample_prop = prop_color === 'borderColor' ? 'backgroundColor' : 'borderColor';
          let _sample_color = this.editableDatasets[dataset_idx][_sample_prop][data_idx];

          const _is_light_color = tinycolor(_sample_color).isLight();
          const _color_alpha = _is_light_color
            ? prop_color === 'borderColor'
              ? 10
              : 15
            : prop_color === 'borderColor'
              ? 0.3
              : 0.6;

          let _prop_new_color = _is_light_color
            ? tinycolor(_sample_color).darken(_color_alpha)
            : tinycolor(_sample_color).setAlpha(_color_alpha);

          this.$set(
            this.editableDatasets[dataset_idx][prop_color],
            data_idx,
            `#${_prop_new_color.toHex8()}`
          );
          this.$emit('updateChartDataSets', [...this.editableDatasets]);
        }
      },

      async getAutomaticColorScheme(editable_data, change_all = false) {
        try {
          if (editable_data === null) {
            const recommendedBorderColorList = await getColorScheme(
              this.selected_report.sub_color,
              this.editableDatasets.length,
              true
            );
            const recommendedBackgroundColorList = await getColorScheme(
              this.selected_report.main_color,
              this.editableDatasets.length,
              true
            );

            this.editableDatasets = this.editableDatasets.map((temp_editable_data, color_index) => {
              return {
                ...temp_editable_data,
                borderColor: Array(temp_editable_data['borderColor'].length).fill(
                  recommendedBorderColorList[color_index]
                ),
                backgroundColor: Array(temp_editable_data['backgroundColor'].length).fill(
                  recommendedBackgroundColorList[color_index]
                ),
              };
            });
          } else {
            if (change_all) {
              const recommendedBorderColorList = await getColorScheme(
                this.selected_report.sub_color,
                editable_data['borderColor'].length,
                true
              );
              editable_data['borderColor'] = recommendedBorderColorList;

              const recommendedBackgroundColorList = await getColorScheme(
                this.selected_report.main_color,
                editable_data['backgroundColor'].length,
                true
              );
              editable_data['backgroundColor'] = recommendedBackgroundColorList;
            } else {
              const recommendedBorderColorList = await getColorScheme(
                this.selected_report.sub_color,
                1,
                true
              );
              editable_data['borderColor'] = Array(editable_data['borderColor'].length).fill(
                recommendedBorderColorList[0]
              );

              const recommendedBackgroundColorList = await getColorScheme(
                this.selected_report.main_color,
                1,
                true
              );
              editable_data['backgroundColor'] = Array(
                editable_data['backgroundColor'].length
              ).fill(recommendedBackgroundColorList[0]);
            }
          }
          this.$emit('updateChartDataSets', [...this.editableDatasets]);
        } catch (error) {
          error.message = '[차트 설정이슈]' + error.message;
          this.$Sentry.captureException(error);
        }
      },
    },
  };
</script>

<style lang="scss">
  .color-setting-card + .color-setting-card {
    margin-left: 15px;
  }
</style>
